import React, { useState, useEffect } from 'react'
import moment from 'moment-timezone'
import { useHistory, useLocation } from 'react-router-dom'
import {
    Button,
    Card,
    Input,
    Icon,
    Heading,
    Paragraph,
    LoadingFull as Loading
} from '@arnsbogroup/react-voicearchive'
import { FormattedMessage, useIntl } from 'react-intl'
import SchedulingPollForm from '../SchedulingPollForm'
import messages from './messages'

const SchedulingPollCreatePage = () => {
    const { formatMessage } = useIntl()
    const history = useHistory()
    const location = useLocation()
    const [order, setOrder] = useState(null)
    const [loadingOrder, setLoadingOrder] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [generatedHashId, setGeneratedHashId] = useState(null)
    const [shareableLink, setShareableLink] = useState(null)

    const queryParams = new URLSearchParams(location.search)
    const orderId = queryParams.get('orderId')
    const duplicateFrom = location.state?.duplicateFrom

    useEffect(() => {
        if (location.pathname === '/scheduling-polls/create') {
            history.replace({
                pathname: '/scheduling-polls/create/calendar',
                search: location.search,
                state: location.state,
            })
        }
    }, [location.pathname, location.search, history])

    useEffect(() => {
        if (!orderId) return
        setLoadingOrder(true)
        fetch(`/rest/v2/order/${orderId}`)
            .then(res => res.json())
            .then(data => {
                if (data) setOrder(data)
                setLoadingOrder(false)
            })
            .catch(() => {
                toastr.error(formatMessage(messages.loadOrderError))
                setLoadingOrder(false)
            })
    }, [orderId])

    const handleSubmit = async ({ title, description, includeBranding, timeSlots }) => {
        setIsSubmitting(true)
        try {
            const timeSlotsUTC = timeSlots.map(slot => ({
                startDatetime: moment(slot.startDatetime).utc().format('YYYY-MM-DD HH:mm:ss'),
                endDatetime: moment(slot.endDatetime).utc().format('YYYY-MM-DD HH:mm:ss'),
            }))

            const body = { title, description, timeSlots: timeSlotsUTC, includeBranding }
            if (orderId) body.orderUid = parseInt(orderId)

            const response = await fetch('/rest/v2/schedulingpoll', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                },
                credentials: 'same-origin',
                body: JSON.stringify(body),
            })

            const data = await response.json()

            if (data.success && data.data?.hashId) {
                setGeneratedHashId(data.data.hashId)
                setShareableLink(data.data.publicUrl || `${window.location.origin}/scheduling-poll/${data.data.hashId}`)
                toastr.success(formatMessage(messages.pollCreatedSuccess))
            } else {
                throw new Error(data.errors ? data.errors.join(', ') : formatMessage(messages.createPollError))
            }
        } catch (error) {
            toastr.error(error.message || formatMessage(messages.createPollError))
        } finally {
            setIsSubmitting(false)
        }
    }

    const copyLinkToClipboard = () => {
        navigator.clipboard.writeText(shareableLink)
            .then(() => toastr.success(formatMessage(messages.linkCopied)))
            .catch(() => toastr.error(formatMessage(messages.linkCopyFailed)))
    }

    if (isSubmitting || loadingOrder) {
        return <Loading />
    }

    if (shareableLink) {
        return (
            <div className="flex-1 px-4 py-4">
                <div className="max-w-2xl mx-auto">
                    <Card className="bg-green-50 border border-green-200 p-6">
                        <div className="flex items-center gap-2 mb-4">
                            <Icon icon={['fas', 'check-circle']} className="text-2xl text-positive" />
                            <Heading level={2} className="text-2xl font-bold text-green-800 mb-0">
                                <FormattedMessage {...messages.pollCreated} />
                            </Heading>
                        </div>
                        <Paragraph className="text-gray-700 mb-4">
                            <FormattedMessage {...messages.shareLink} />
                        </Paragraph>
                        <div className="flex gap-2 mb-6">
                            <Input value={shareableLink} readOnly className="flex-1" />
                            <Button onClick={copyLinkToClipboard}>
                                <FormattedMessage {...messages.copyLink} />
                            </Button>
                        </div>
                        <div className="flex gap-3">
                            <Button
                                onClick={() => {
                                    setGeneratedHashId(null)
                                    setShareableLink(null)
                                }}
                                variant="outline"
                            >
                                <FormattedMessage {...messages.createAnother} />
                            </Button>
                            <Button onClick={() => history.push(`/scheduling-polls/${generatedHashId}`)}>
                                <FormattedMessage {...messages.viewDetails} />
                            </Button>
                            <Button onClick={() => history.push('/scheduling-polls')} variant="outline">
                                <FormattedMessage {...messages.viewAllPolls} />
                            </Button>
                        </div>
                    </Card>
                </div>
            </div>
        )
    }

    const initialTitle = duplicateFrom?.title
        ?? (order ? formatMessage(messages.availabilityFor, { orderName: order.name || `#${order.uid}` }) : '')

    return (
        <div className="flex-1 px-4 py-4">
            <div className="w-full mx-auto">
                <Heading level={1} className="text-2xl font-bold mb-4">
                    <FormattedMessage {...messages.createSchedulingPoll} />
                </Heading>

                {order && (
                    <div className="bg-blue-50 border border-blue-200 rounded p-4 mb-4">
                        <Paragraph className="text-sm text-blue-900 mb-0">
                            <Icon icon={['fas', 'link']} className="mr-2" />
                            <FormattedMessage
                                {...messages.creatingForOrder}
                                values={{ orderName: <strong>{order.name || `#${order.uid}`}</strong> }}
                            />
                        </Paragraph>
                    </div>
                )}

                <SchedulingPollForm
                    key={duplicateFrom ? `duplicate-${initialTitle}` : orderId || 'create'}
                    initialTitle={initialTitle}
                    initialDescription={duplicateFrom?.description ?? ''}
                    initialIncludeBranding={duplicateFrom?.includeBranding ?? true}
                    initialTimeSlots={duplicateFrom?.timeSlots ?? []}
                    basePath="/scheduling-polls/create"
                    onSubmit={handleSubmit}
                    isSubmitting={isSubmitting}
                    submitLabel={formatMessage(messages.createPoll)}
                    onCancel={() => history.push('/scheduling-polls')}
                />
            </div>
        </div>
    )
}

export default SchedulingPollCreatePage
